import { useState, useRef, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Send } from "lucide-react";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  centered?: boolean;
}

export const ChatInput = ({ onSendMessage, isLoading, inputRef, centered }: ChatInputProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const localInputRef = useRef<HTMLInputElement>(null);
  const actualInputRef = inputRef || localInputRef;

  const handleSend = () => {
    if (inputValue.trim() === "" || isLoading) return;
    onSendMessage(inputValue);
    setInputValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-border/60 p-3">
      <div className={cn(
        "flex items-center gap-2 rounded-sm border border-border bg-background/60 py-1 pl-3 pr-1.5 transition-colors focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/30",
        centered && "mx-auto max-w-3xl"
      )}>
        <Input
          ref={actualInputRef}
          placeholder={t("chat.placeholder")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          className="h-8 flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <Button
          onClick={handleSend}
          disabled={isLoading || inputValue.trim() === ""}
          size="icon"
          aria-label={t("chat.send")}
          className="h-8 w-8 shrink-0 rounded-sm bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-40"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
